import { useEffect, useState } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
const { fetch: originalFetch } = window;

import Loader from './common/Loader';
import PageTitle from './components/PageTitle';
import SignIn from './pages/Authentication/SignIn';
import SignUp from './pages/Authentication/SignUp';
import ForgotPassword from './pages/Authentication/ForgotPassword';
import SelectGame from './pages/Dashboard/SelectGame';
import AuthLayout from './layout/AuthLayout';
import DashboardResearcher from './pages/Dashboard/Sornthai/Researcher/DashboardResearcher';
import DashboardParent from './pages/Dashboard/Sornthai/Parents/DashboardParent';
import DefaultLayout from './layout/DefaultLayout';
import SignInInternal from './pages/Authentication/SignInInternal';
import DashboardTeacher from './pages/Dashboard/Sornthai/Teachers/DashboardTeacher';
import UserDataStages from './pages/Dashboard/Sornthai/Teachers/UserDataStages';
import useLocalStorage from './hooks/useLocalStorage';
import CreateUser from './pages/User/CreateUser';
import useUser from './hooks/user';
import Users from './pages/Dashboard/Sornthai/Admin/Users';
import SearchUser from './pages/Dashboard/Sornthai/Admin/SearchUser';
import EditUser from './pages/User/EditUser';
import ProtectedRoute from './pages/Authentication/ProtectedRoute';
import Profile from './pages/Dashboard/Sornthai/Profile';
import ResetPassword from './pages/Authentication/ResetPassword';
import GameManual from './pages/GameManual';

import ResearcherUserDataStages from './pages/Dashboard/Sornthai/Researcher/ResearcherUserDataStages';
import UserInfo from './pages/Dashboard/Sornthai/Parents/UserInfo';

window.fetch = async (resource, options: any = {}) => {
  let headers: any = {
    'Content-Type': 'application/json',
  };

  const token = JSON.parse(window.localStorage.getItem('token') as string);

  if (!options.headers && token) headers['Authorization'] = `Bearer ${token}`;

  options.headers = {
    ...options.headers,
    ...headers,
  };

  const response = await originalFetch(resource, options);

  // if (!response.ok && response.status === 404) {
  //   // 404 error handling
  //   return Promise.reject(response);
  // }
  return response;
};

const rolePathMap: Record<number, string> = {
  0: 'dashboard-parent',
  1: 'dashboard-parent',
  2: 'dashboard-parent',
  3: 'dashboard-teacher',
  4: 'dashboard-researcher',
  5: 'admin/users',
  6: 'admin/users',
};

function App() {
  const [token] = useLocalStorage('token', '');
  const user = useUser('token');
  const currentRole =
    user?.role !== undefined && user?.role !== null
      ? Number(user.role)
      : undefined;

  const [loading, setLoading] = useState<boolean>(true);
  const [isUserLoaded, setIsUserLoaded] = useState<boolean>(false);

  const { pathname, search } = useLocation();
  const teacherId = new URLSearchParams(search).get('teacherid');
  const parentId = new URLSearchParams(search).get('parentid');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  useEffect(() => {
    setTimeout(() => setLoading(false), 1000);
  }, []);

  // Check if user data is loaded
  useEffect(() => {
    if (user?.role !== undefined) {
      setIsUserLoaded(true);
    }
  }, [user?.role]);

  const getNavigatePath = () => {
    if (!token) return '/signin';
    if (!isUserLoaded || currentRole === undefined || Number.isNaN(currentRole))
      return '/signin';
    return `/sornthai/${rolePathMap[currentRole]}`;
  };

  return loading ? (
    <Loader />
  ) : (
    <>
      <Routes>
        <Route path="/" element={<Navigate to={getNavigatePath()} />} />

        <Route path="game-manual" element={<GameManual />} />
        <Route path="game-manual/:phase" element={<GameManual />} />

        {/* Authentication */}
        <Route path="/" element={<DefaultLayout />}>
          <Route
            path="signin"
            element={
              <>
                <PageTitle title="เข้าสู่ระบบ" />
                <SignIn />
              </>
            }
          />
          <Route
            path="signup"
            element={
              <>
                <PageTitle title="สมัครบัญชีผู้ใช้" />
                <SignUp />
              </>
            }
          />
          <Route
            path="forgot-password"
            element={
              <>
                <PageTitle title="ลืมรหัสผ่าน" />
                <ForgotPassword />
              </>
            }
          />
          <Route
            path="reset-password"
            element={
              <>
                <PageTitle title="ตั้งรหัสใหม่" />
                <ResetPassword />
              </>
            }
          />
          <Route
            path="internal-signin"
            element={
              <>
                <PageTitle title="เข้าสู่ระบบ" />
                <SignInInternal />
              </>
            }
          />
        </Route>

        <Route
          path="/select-game"
          element={
            <>
              <PageTitle title="เลือกเกม" />
              <SelectGame />
            </>
          }
        />

        {/* sornthai app */}
        <Route path="/sornthai" element={<AuthLayout />}>
          <Route
            path="/sornthai"
            element={
              <Navigate
                to={
                  isUserLoaded &&
                  currentRole !== undefined &&
                  !Number.isNaN(currentRole)
                    ? rolePathMap[currentRole]
                    : '/signin'
                }
              />
            }
          />

          <Route
            path="search-user"
            element={
              <>
                <PageTitle title="ค้นหาผู้ใช้งาน" />
                <SearchUser />
              </>
            }
          />

          <Route
            path="profile"
            element={
              <>
                <PageTitle title="โปรไฟล์" />
                <Profile />
              </>
            }
          />

          {/* Admin, Superadmin */}
          <Route path="admin" element={<ProtectedRoute roles={[4, 5, 6]} />}>
            <Route
              path="users"
              element={
                <>
                  <PageTitle title="ผู้ใช้งาน" />
                  <Users />
                </>
              }
            />

            <Route
              path="create-user"
              element={
                <>
                  <PageTitle title="Create User" />
                  <CreateUser />
                </>
              }
            />

            <Route
              path="edit-user/:userid"
              element={
                <>
                  <PageTitle title="Edit User" />
                  <EditUser />
                </>
              }
            />
          </Route>

          {/* Researcher, Admin, Superadmin */}
          <Route path="" element={<ProtectedRoute roles={[4, 5, 6]} />}>
            <Route
              path="dashboard-researcher"
              element={
                <>
                  <PageTitle title="Dashboard Researcher" />
                  <DashboardResearcher />
                </>
              }
            />

            <Route
              path="dashboard-researcher/user-data-stages/:userid"
              element={
                <>
                  <PageTitle title="Dashboard Researcher" />
                  <ResearcherUserDataStages />
                </>
              }
            />
          </Route>

          {/* Teacher, Researcher, Admin, Superadmin */}
          <Route path="" element={<ProtectedRoute roles={[3, 4, 5, 6]} />}>
            <Route
              path="dashboard-teacher/user-data-stages/:userid"
              element={
                <>
                  <PageTitle title="User data stages" />
                  <UserDataStages />
                </>
              }
            />

            <Route
              path="dashboard-teacher"
              element={
                currentRole === 4 && !teacherId ? (
                  <Navigate to={`/sornthai/search-user?role=3`} replace />
                ) : (
                  <>
                    <PageTitle title="Dashboard Teacher" />
                    <DashboardTeacher />
                  </>
                )
              }
            />

            <Route
              path="dashboard-teacher/create-student"
              element={
                <>
                  <PageTitle title="Create Student" />
                  <CreateUser />
                </>
              }
            />

            <Route
              path="dashboard-teacher/edit-student/:userid"
              element={
                <>
                  <PageTitle title="Edit User" />
                  <EditUser />
                </>
              }
            />
          </Route>

          {/* Teacher, Researcher, Admin, Superadmin */}
          <Route
            path=""
            element={<ProtectedRoute roles={[0, 1, 2, 3, 4, 5, 6]} />}
          >
            <Route
              path="dashboard-parent"
              element={
                currentRole === 4 && !parentId ? (
                  <Navigate to={`/sornthai/search-user?role=2`} replace />
                ) : (
                  <>
                    <PageTitle title="Dashboard Parent" />
                    <DashboardParent />
                  </>
                )
              }
            />

            <Route
              path="dashboard-parent/user-info/:userid"
              element={
                <>
                  <PageTitle title="User Info" />
                  <UserInfo />
                </>
              }
            />

            <Route
              path="dashboard-parent/create-student"
              element={
                <>
                  <PageTitle title="Create Student" />
                  <CreateUser />
                </>
              }
            />

            <Route
              path="dashboard-parent/edit-student/:userid"
              element={
                <>
                  <PageTitle title="Edit User" />
                  <EditUser />
                </>
              }
            />
          </Route>
        </Route>
      </Routes>
    </>
  );
}

export default App;
